import { useState, useMemo } from 'react';
import { useAccount, useReadContract } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { parseUnits } from 'viem';
import { Button } from '../components/ui/Button';
import { Card, StatCard, Badge } from '../components/ui/Card';
import { Input, FormField } from '../components/ui/Input';
import { useBuyFlow, useTxReceipt } from '../hooks/useBuyFlow';
import { usePresaleStats } from '../hooks/usePresaleStats';
import { PRESALE_ABI, PRESALE_ADDRESS, USDT_DECIMALS } from '../lib/contracts';
import { fmtAiml, fmtUsdt } from '../lib/format';
import { useToast } from '../contexts/ToastContext';

export default function Buy() {
  const { address, isConnected } = useAccount();
  const [usdt, setUsdt] = useState('100');
  const presale = usePresaleStats();
  const flow = useBuyFlow();
  const receipt = useTxReceipt(flow.txHash ?? undefined);
  const toast = useToast();

  const referralCode =
    typeof window !== 'undefined' ? localStorage.getItem('altcoins.refCode') ?? '' : '';

  const usdtNum = parseFloat(usdt) || 0;

  const usdtRaw = useMemo(() => {
    try {
      if (!usdt || usdtNum <= 0) return 0n;
      return parseUnits(usdt, USDT_DECIMALS);
    } catch {
      return 0n;
    }
  }, [usdt, usdtNum]);

  const quote = useReadContract({
    address: PRESALE_ADDRESS,
    abi: PRESALE_ABI,
    functionName: 'getTokenAmountUSDT',
    args: usdtRaw > 0n ? [usdtRaw] : undefined,
    query: { enabled: usdtRaw > 0n },
  });

  const tokensOut = useMemo(() => {
    if (quote.data === undefined) return 0;
    // Contract returns AIML in whole token units.
    return Number(quote.data as bigint);
  }, [quote.data]);

  // Any positive amount is valid. Affiliate tier qualification is separate
  // (requires $150 personal spend, communicated via the dashboard).
  const canBuy = usdtNum > 0;
  const isWorking =
    flow.step === 'approving' ||
    flow.step === 'approve-confirming' ||
    flow.step === 'buying' ||
    flow.step === 'buy-confirming' ||
    flow.step === 'attributing';

  async function handleBuy() {
    if (!isConnected || !address) {
      toast.error('Connect a wallet first');
      return;
    }
    if (presale.paused) {
      toast.error('Sales are paused right now. Please try later.');
      return;
    }
    await flow.buy(usdt, referralCode || undefined);
    if (flow.step === 'done') {
      toast.success('Purchase recorded. The indexer will pick it up shortly.');
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-12">
        <div className="eyebrow mb-3">PRESALE BUY</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Buy <span className="text-gradient">$AIML</span> with USDT
        </h1>
        <p className="mt-3 text-ink-600">
          Polygon mainnet. Connect your wallet, approve once, buy in seconds.
        </p>
      </div>

      {presale.paused && (
        <div className="max-w-2xl mx-auto mb-8 p-4 rounded-2xl bg-warn/5 border border-warn/30 text-center">
          <div className="font-display font-semibold text-warn">Presale paused</div>
          <p className="text-sm text-ink-700 mt-1">
            Token sales are currently paused. Check back soon.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="RAISED"
          value={presale.totalUsdtRaised !== null ? fmtUsdt(presale.totalUsdtRaised) : '—'}
          accent="violet"
        />
        <StatCard
          label="RATE"
          value={presale.tokensPerUsdt !== null ? fmtAiml(presale.tokensPerUsdt) : '—'}
          hint="AIML / USDT"
          accent="magenta"
        />
        <StatCard
          label="SOLD"
          value={presale.totalAimlSold !== null ? fmtAiml(presale.totalAimlSold) : '—'}
          hint="AIML"
          accent="azure"
        />
      </div>

      {presale.error && (
        <div className="mb-6 p-4 rounded-2xl bg-danger/5 border border-danger/30">
          <div className="font-display font-semibold text-danger mb-1">
            Couldn't read contract data
          </div>
          <div className="text-xs font-mono text-ink-700 break-all">
            {(presale.error as Error).message || String(presale.error)}
          </div>
        </div>
      )}

      <Card className="!p-8">
        {referralCode && (
          <div className="mb-6 p-3 rounded-2xl bg-violet-500/5 border border-violet-500/20 flex items-center justify-between">
            <div className="text-sm">
              <span className="font-mono text-violet-600 font-semibold">{referralCode}</span>
              <span className="text-ink-600 ml-2">applied as your referrer</span>
            </div>
            <Badge kind="info">REF</Badge>
          </div>
        )}

        <FormField
          label="Amount in USDT"
          hint={
            quote.isLoading
              ? 'Fetching quote from contract…'
              : tokensOut > 0
                ? `You'll receive ≈ ${fmtAiml(tokensOut)} AIML`
                : 'Enter any positive amount in USDT'
          }
        >
          <div className="relative">
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={usdt}
              onChange={(e) => setUsdt(e.target.value)}
              placeholder="100"
              className="!h-16 !text-2xl !pr-24 font-display"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 font-mono font-semibold text-ink-600">
              USDT
            </div>
          </div>
        </FormField>

        <div className="mt-6 p-5 rounded-2xl bg-paper-200 flex items-center justify-between">
          <div>
            <div className="eyebrow mb-1">YOU RECEIVE</div>
            <div className="font-display text-3xl font-bold text-gradient tabular-nums">
              {fmtAiml(tokensOut)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-ink-600 font-mono">AIML</div>
            <div className="text-xs text-ink-600 mt-1">live quote</div>
          </div>
        </div>

        {/* Affiliate tier nudge (informational, doesn't block buying) */}
        <div className="mt-4 p-3 rounded-2xl bg-violet-500/5 border border-violet-500/10 text-xs text-ink-700">
          💡 Buy $150+ to become a <strong>Bronze Affiliate</strong> · $300+ to upgrade to{' '}
          <strong>Silver</strong>. Unlocks your referral link and 15–20% commission on every
          friend you bring.
        </div>

        <div className="mt-6">
          {!isConnected ? (
            <ConnectButton.Custom>
              {({ openConnectModal }) => (
                <Button size="lg" className="w-full" onClick={openConnectModal}>
                  Connect wallet to buy
                </Button>
              )}
            </ConnectButton.Custom>
          ) : (
            <Button
              size="lg"
              className="w-full"
              onClick={handleBuy}
              isLoading={isWorking}
              disabled={!canBuy || isWorking || presale.paused}
            >
              {flowLabel(flow.step) ||
                (canBuy
                  ? `Buy ${fmtAiml(tokensOut)} AIML for ${fmtUsdt(usdtNum)}`
                  : 'Enter an amount')}
            </Button>
          )}
        </div>

        {flow.errorMessage && (
          <div className="mt-4 p-3 rounded-2xl bg-danger/5 border border-danger/20 text-danger text-sm">
            {flow.errorMessage}
            <button onClick={flow.reset} className="ml-3 underline">
              Reset
            </button>
          </div>
        )}

        {flow.step === 'done' && flow.txHash && (
          <div className="mt-4 p-4 rounded-2xl bg-success/5 border border-success/20">
            <div className="font-display font-semibold text-success">Purchase submitted</div>
            <div className="text-xs text-ink-600 mt-2 break-all font-mono">{flow.txHash}</div>
            <div className="mt-2 text-sm">
              <a
                href={`https://polygonscan.com/tx/${flow.txHash}`}
                target="_blank"
                rel="noreferrer"
                className="text-violet-600 underline"
              >
                View on Polygonscan
              </a>
              {receipt.data && (
                <span className="ml-3 text-success">
                  · Confirmed in block {Number(receipt.data.blockNumber)}
                </span>
              )}
            </div>
          </div>
        )}
      </Card>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">USDT goes direct</div>
          <p className="text-ink-600 text-xs">
            Your USDT transfers to the project's ops wallet on Polygon — verifiable on
            Polygonscan.
          </p>
        </div>
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">Affiliate program</div>
          <p className="text-ink-600 text-xs">
            Buy $150+ to unlock your referral link. Earn 15-22% USDT cash + AIML bonus on every
            friend's purchase.
          </p>
        </div>
        <div className="card-base p-5">
          <div className="font-display font-semibold text-ink mb-1">Vesting starts at TGE</div>
          <p className="text-ink-600 text-xs">
            AIML tokens unlock linearly over six months from token generation event, including
            bonuses.
          </p>
        </div>
      </div>
    </div>
  );
}

function flowLabel(step: string): string | null {
  switch (step) {
    case 'approving':
      return 'Approve USDT in your wallet…';
    case 'approve-confirming':
      return 'Waiting for approval confirmation…';
    case 'buying':
      return 'Confirm purchase in your wallet…';
    case 'buy-confirming':
      return 'Waiting for buy confirmation…';
    case 'attributing':
      return 'Recording attribution…';
    default:
      return null;
  }
}
